import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Grid3X3, List, ExternalLink, Send, Users, Shield,
  MapPin, AlertTriangle, ChevronLeft, ChevronRight, Eye, Lock,
  X, Calendar, Ruler, Weight
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

const PersonCard = ({ person, isGrid, user, navigate }) => {
  const hasPhoto = person.photo_url;
  const isLocked = !user;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      className={`group relative bg-slate-900/80 border border-slate-800 hover:border-pink-500/40 rounded-sm overflow-hidden transition-all ${isGrid ? "" : "flex gap-0"}`}
      data-testid={`person-card-${person.namus_id}`}
    >
      {/* Photo */}
      <div className={`relative overflow-hidden bg-slate-800 ${isGrid ? "aspect-[3/4]" : "w-28 h-36 flex-shrink-0"}`}>
        {hasPhoto ? (
          <img
            src={`${BACKEND_URL}${person.photo_url}`}
            alt={person.name}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={`${hasPhoto ? 'hidden' : 'flex'} w-full h-full items-center justify-center bg-slate-800`}>
          <Users className="w-10 h-10 text-slate-600" />
        </div>

        {/* Badge */}
        <div className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold tracking-wider bg-pink-600 text-white rounded-sm shadow-lg shadow-pink-500/20">
          MISSING
        </div>

        {/* NamUs ID */}
        <div className="absolute bottom-2 left-2 px-1.5 py-0.5 text-[9px] font-mono tracking-wider bg-black/60 text-slate-300 rounded-sm backdrop-blur-sm">
          {person.namus_id}
        </div>
      </div>

      {/* Info */}
      <div className={`p-3 flex flex-col ${isGrid ? "" : "flex-1 justify-between"}`}>
        <div>
          <h3 className="font-heading font-bold text-white text-sm leading-tight mb-1 group-hover:text-pink-400 transition-colors line-clamp-2">
            {person.name}
          </h3>

          {/* Age & Gender */}
          {(person.age_min || person.gender) && (
            <p className="text-slate-400 text-xs mb-1">
              {person.age_min && (
                <span>Age: {person.age_min === person.age_max ? person.age_min : `${person.age_min}-${person.age_max}`}</span>
              )}
              {person.age_min && person.gender && ' | '}
              {person.gender && <span>{person.gender}</span>}
            </p>
          )}

          {/* Location */}
          {(person.city_last_contact || person.state_last_contact) && (
            <div className="flex items-center gap-1 text-slate-500 text-xs mb-1.5">
              <MapPin className="w-3 h-3" />
              <span>{[person.city_last_contact, person.state_last_contact].filter(Boolean).join(', ')}</span>
            </div>
          )}

          {/* Physical description - blurred for non-auth */}
          {isGrid && (person.hair_color || person.eye_color) && (
            <div className={`text-xs text-slate-400 space-y-0.5 ${isLocked ? "blur-[3px] select-none" : ""}`}>
              {person.hair_color && <p>Hair: {person.hair_color}</p>}
              {person.eye_color && <p>Eyes: {person.eye_color}</p>}
            </div>
          )}

          {/* Date of last contact */}
          {person.date_last_contact && (
            <div className={`flex items-center gap-1 text-xs mt-1 ${isLocked ? "text-slate-600" : "text-slate-500"}`}>
              <Calendar className="w-3 h-3" />
              <span>{isLocked ? "Login for date info" : person.date_last_contact}</span>
            </div>
          )}

          {/* Locked message */}
          {isLocked && (
            <div className="flex items-center gap-1 mt-1.5 text-pink-400 text-[10px] font-mono">
              <Lock className="w-3 h-3" />
              <span>Login for full details</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={`flex gap-2 mt-2`}>
          <a
            href={person.details_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-pink-400 transition-colors font-mono tracking-wider"
            data-testid={`person-namus-link-${person.namus_id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-3 h-3" />
            NamUs
          </a>
          <button
            onClick={() => navigate(user ? '/tips' : '/login')}
            className="flex items-center gap-1 text-[10px] text-amber-400 hover:text-amber-300 transition-colors font-mono tracking-wider ml-auto"
            data-testid={`person-info-btn-${person.namus_id}`}
          >
            <Send className="w-3 h-3" />
            HAVE INFO?
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default function MissingPersonsPage() {
  const navigate = useNavigate();
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const LIMIT = 24;

  const [user, setUser] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (token && storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch { setUser(null); }
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPersons = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", LIMIT);
      params.set("skip", page * LIMIT);
      if (debouncedSearch) params.set("search", debouncedSearch);
      const res = await api.get(`/missing-persons?${params.toString()}`);
      setPersons(res.data.persons || []);
      setTotal(res.data.total || 0);
    } catch {
      setPersons([]);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => { fetchPersons(); }, [fetchPersons]);
  useEffect(() => { setPage(0); }, [debouncedSearch]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="missing-persons-page">
      {/* Top nav */}
      <div className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-to-home">
            <ChevronLeft className="w-4 h-4" />
            <span className="font-heading font-bold text-lg tracking-tight text-white">WYSSPR</span>
          </button>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/state-fugitives")} className="text-xs font-mono text-slate-400 hover:text-cyan-400 tracking-wider hidden sm:block" data-testid="nav-state-fugitives">
              FUGITIVES
            </button>
            {user ? (
              <button onClick={() => navigate("/dashboard")} className="text-xs font-mono text-cyan-400 hover:text-cyan-300 tracking-wider" data-testid="nav-dashboard">
                DASHBOARD
              </button>
            ) : (
              <button onClick={() => navigate("/login")} className="bg-pink-500 text-white text-xs font-bold px-4 py-1.5 rounded-sm hover:bg-pink-400 transition-colors tracking-wider" data-testid="nav-login">
                LOGIN
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-pink-950/20 via-slate-950 to-slate-950" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Users className="w-5 h-5 text-pink-400" />
              <p className="text-pink-400 font-mono text-sm tracking-[0.3em]">NamUs DATABASE</p>
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase text-white mb-4">
              Missing Persons
            </h1>
            <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto mb-3">
              Help reunite families. <span className="text-white font-bold">{total.toLocaleString()}</span> missing persons
              from the National Missing and Unidentified Persons System.
            </p>
            {!user && (
              <p className="text-pink-400 text-xs font-mono tracking-wider">
                <Lock className="w-3 h-3 inline mr-1" />
                Login for full details including physical descriptions and circumstances
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Search & Controls */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search missing persons by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-slate-900 border border-slate-700 rounded-sm text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-pink-500 transition-colors font-mono"
              data-testid="mp-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex border border-slate-700 rounded-sm overflow-hidden">
            <button onClick={() => setViewMode("grid")}
              className={`p-2.5 transition-colors ${viewMode === "grid" ? "bg-pink-500/20 text-pink-400" : "bg-slate-900 text-slate-500 hover:text-slate-300"}`}
              data-testid="mp-view-grid">
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode("list")}
              className={`p-2.5 transition-colors ${viewMode === "list" ? "bg-pink-500/20 text-pink-400" : "bg-slate-900 text-slate-500 hover:text-slate-300"}`}
              data-testid="mp-view-list">
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="max-w-7xl mx-auto px-4 mb-4">
        <div className="flex items-center justify-between">
          <p className="text-slate-500 text-xs font-mono">
            {loading ? "Searching..." : `${total.toLocaleString()} missing persons`}
            {debouncedSearch && " (filtered)"}
          </p>
          <div className="flex items-center gap-1.5">
            <span className="px-2 py-0.5 text-[10px] font-mono tracking-wider bg-pink-600/20 border border-pink-500/30 text-pink-400 rounded-sm">NamUs</span>
          </div>
        </div>
      </div>

      {/* Grid/List */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="bg-slate-900/80 border border-slate-800 rounded-sm animate-pulse overflow-hidden">
                <div className="aspect-[3/4] bg-slate-800" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-slate-800 rounded w-3/4" />
                  <div className="h-2 bg-slate-800 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : persons.length === 0 ? (
          <div className="text-center py-20">
            <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 font-heading text-lg">No missing persons found</p>
            <p className="text-slate-600 text-sm mt-1">Try adjusting your search</p>
          </div>
        ) : (
          <div className={viewMode === "grid"
            ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
            : "flex flex-col gap-2"
          }>
            <AnimatePresence mode="popLayout">
              {persons.map((p, i) => (
                <PersonCard key={`${p.namus_id}-${i}`} person={p} isGrid={viewMode === "grid"} user={user} navigate={navigate} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="max-w-7xl mx-auto px-4 pb-12">
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
              className="flex items-center gap-1 px-4 py-2 border border-slate-700 rounded-sm text-xs font-mono text-slate-400 hover:border-pink-500 hover:text-pink-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="mp-prev-page">
              <ChevronLeft className="w-3 h-3" /> PREV
            </button>
            <span className="text-xs font-mono text-slate-500">PAGE {page + 1} OF {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
              className="flex items-center gap-1 px-4 py-2 border border-slate-700 rounded-sm text-xs font-mono text-slate-400 hover:border-pink-500 hover:text-pink-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="mp-next-page">
              NEXT <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* CTA for non-logged-in users */}
      {!user && (
        <section className="py-16 px-4 bg-gradient-to-t from-slate-950 via-pink-950/10 to-slate-950 border-t border-slate-800">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white uppercase mb-3">
              Help Bring Them Home
            </h2>
            <p className="text-slate-400 mb-6">
              Create a free account to access full missing person profiles and submit tips.
            </p>
            <button onClick={() => navigate("/login")} className="bg-pink-500 hover:bg-pink-400 text-white font-bold px-8 py-3 rounded-sm tracking-wider transition-colors" data-testid="mp-cta-signup">
              Sign Up Free
            </button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-slate-600 font-mono">
          <span>&copy; 2026 Wysspr Intelligence</span>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/state-fugitives")} className="hover:text-slate-400 transition-colors">Fugitives</button>
            <button onClick={() => navigate("/")} className="hover:text-slate-400 transition-colors">Home</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
